import type { InstaQLEntity } from "@instantdb/admin";
import { toast } from "react-hot-toast";
import { apiClient } from "../../constants.ts";
import schema from "../../instant.schema.ts";
import { BotNotFound, Button, PageSpinner } from "./components.tsx";
import { useAuth, useQuery } from "./db.ts";

type Thread = InstaQLEntity<typeof schema, "projects">;

const viewChatLinkForThread = (groupId: string, conversation: string) =>
  `https://view-chat.com/conversations?groupId=${groupId}&conversationName=${
    encodeURIComponent(conversation)
  }`;

const StopThreadButton = (
  { threadId }: { threadId: string },
) => {
  const token = useAuth().user?.refresh_token ?? "";
  return (
    <Button
      className="text-xs px-2 py-1 bg-danger hover:bg-danger-hover"
      onClick={() =>
        toast.promise(
          apiClient({
            endpoint: "stop-thread",
            token,
            payload: { projectId: threadId },
          }),
          {
            loading: "Stopping...",
            success: "Thread stopped",
            error: "Failed to stop thread",
          },
        )}
    >
      Stop
    </Button>
  );
};

export const Threads = ({ id }: { id: string }) => {
  const { data, isLoading, error } = useQuery({
    bots: { $: { where: { id } }, projects: {} },
  });
  if (isLoading) return <PageSpinner />;
  if (error) return <div>Error loading threads: {error.message}</div>;
  if (!data.bots.length) return <BotNotFound />;
  const bot = data.bots[0];
  const threads: Thread[] = bot.projects || [];
  return (
    <div className="max-h-[600px] overflow-y-auto">
      {threads.length === 0
        ? <div>No threads yet.</div>
        : (
          <div className="overflow-x-auto rounded-lg bg-input-light dark:bg-input">
            <table className="min-w-full divide-y divide-border-subtle-light dark:divide-border-subtle">
              <thead>
                <tr>
                  <th className="sticky top-0 z-10 px-4 py-2 text-left bg-input-light dark:bg-input">
                    ID
                  </th>
                  <th className="sticky top-0 z-10 px-4 py-2 text-left bg-input-light dark:bg-input">
                    Name
                  </th>
                  <th className="sticky top-0 z-10 px-4 py-2 text-left bg-input-light dark:bg-input">
                    Stopped
                  </th>
                  <th className="sticky top-0 z-10 px-4 py-2 text-left bg-input-light dark:bg-input">
                    Updated
                  </th>
                  <th className="sticky top-0 z-10 px-4 py-2 text-left bg-input-light dark:bg-input">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {threads.map((p) => (
                  <tr
                    key={p.id}
                    className="border-b border-border-subtle-light dark:border-border-subtle"
                  >
                    <td className="px-4 py-2">{p.id}</td>
                    <td className="px-4 py-2">{p.name}</td>
                    <td className="px-4 py-2">{p.stopped ? "Yes" : "No"}</td>
                    <td className="px-4 py-2">
                      {p.updatedAt
                        ? new Date(Number(p.updatedAt)).toLocaleString()
                        : ""}
                    </td>
                    <td className="px-4 py-2 flex gap-2">
                      <Button
                        className="text-xs px-2 py-1"
                        onClick={() =>
                          globalThis.open(
                            viewChatLinkForThread(bot.viewChatGroupId, p.id),
                            "_blank",
                          )}
                      >
                        View Chat
                      </Button>
                      {!p.stopped && <StopThreadButton threadId={p.id} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
};
